import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps } from 'react';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';

import { Title } from '../LastFixtureStoryStep/Title';
import { useResultsStoryAssets } from '../LastFixtureStoryStep/useResultsStoryAssets';
import { LeaderboardDivisionChangeRecap } from '../LeaderboardDivisionChangeRecap';
import { Promotion_so5Fixture } from './__generated__/index.graphql';

type Props = {
  so5Fixture?: Promotion_so5Fixture;
  onNext?: ComponentProps<typeof LastFixtureStoryStep>['onNext'];
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
};

export const Promotion = ({ so5Fixture, onNext, onClose }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const { intro } = useResultsStoryAssets();

  const displayedRankings =
    so5Fixture?.mySo5Rankings.filter(
      ranking => ranking.eligibleDivisionChange === DivisionChange.PROMOTED
    ) || [];
  const itemToDisplayOnDesktop = Math.min(3, displayedRankings.length);
  const itemToDisplay = isTabletAndAbove ? itemToDisplayOnDesktop : 1;

  return (
    <LastFixtureStoryStep
      title={<Title type={DivisionChange.PROMOTED} so5Fixture={so5Fixture} />}
      video={{ asset: intro, color: '#00FF18', colorDelay: 800 }}
      onNext={onNext}
      onClose={onClose}
    >
      <div
        style={{
          width: `calc(${itemToDisplay} * 250px + var(--double-unit) * ${itemToDisplay - 1})`,
        }}
      >
        <Scrollable
          withMask
          itemToDisplay={itemToDisplay}
          visibleVerticalOverflowSize={100}
        >
          {displayedRankings.map(ranking => (
            <LeaderboardDivisionChangeRecap
              key={ranking.id}
              so5Ranking={ranking}
            />
          ))}
        </Scrollable>
      </div>
    </LastFixtureStoryStep>
  );
};

Promotion.fragments = {
  so5Fixture: gql`
    fragment Promotion_so5Fixture on So5Fixture {
      slug
      mySo5Rankings {
        id
        eligibleDivisionChange
        ...LeaderboardDivisionChangeRecap_so5Ranking
      }
      ...Title_so5Fixture
    }
    ${LeaderboardDivisionChangeRecap.fragments.so5Ranking}
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<Promotion_so5Fixture>,
};
